import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Database, Trash2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const MODULE_LABELS = {
  house_prices: "House Prices",
  planning_applications: "Planning Applications",
  planning_appeals: "Planning Appeals",
};
const MODULE_FILTERS = [null, "house_prices", "planning_applications", "planning_appeals"];

function formatDMY(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Admin → Integrations → Planning → Cache. Controls the shared cache used
 * by the 3 public website modules (House Prices, Planning Applications,
 * Planning Appeals) — saves repeat AI/API costs by reusing a previous
 * response for the same postcode/search if it isn't older than the
 * configured number of days.
 */
export default function CacheSettingsSection() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [maxAgeDays, setMaxAgeDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [moduleFilter, setModuleFilter] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(true);

  const loadEntries = useCallback(() => {
    setEntriesLoading(true);
    axios
      .get(`${API}/admin/public-tools-cache`, { params: moduleFilter ? { module: moduleFilter } : {}, withCredentials: true })
      .then(({ data }) => {
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      })
      .catch((e) => console.error("Failed to load cache entries:", e))
      .finally(() => setEntriesLoading(false));
  }, [moduleFilter]);

  useEffect(() => {
    axios.get(`${API}/admin/site-styles`, { withCredentials: true })
      .then(({ data }) => {
        const styles = data?.styles || {};
        setEnabled(styles.publicToolsCacheEnabled !== false);
        setMaxAgeDays(styles.publicToolsCacheMaxAgeDays || 30);
      })
      .catch((e) => console.error("Failed to load cache settings:", e))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const save = async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-styles`, { styles: next }, { withCredentials: true });
      toast.success("Cache settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save cache settings");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (checked) => {
    setEnabled(checked);
    save({ publicToolsCacheEnabled: checked });
  };

  const handleDaysChange = (e) => {
    const value = Math.max(1, Math.min(365, Number(e.target.value) || 1));
    setMaxAgeDays(value);
  };

  const handleDaysBlur = () => save({ publicToolsCacheMaxAgeDays: maxAgeDays });

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/public-tools-cache/${id}`, { withCredentials: true });
      toast.success("Cache entry cleared");
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear cache entry");
    }
  };

  const clearAll = async () => {
    try {
      const { data } = await axios.delete(`${API}/admin/public-tools-cache`, {
        params: moduleFilter ? { module: moduleFilter } : {},
        withCredentials: true,
      });
      toast.success(`Cleared ${data.deleted} cached entr${data.deleted === 1 ? "y" : "ies"}`);
      loadEntries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear cache");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <Card data-testid="cache-settings-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" /> Cache
        </CardTitle>
        <CardDescription>
          Applies to the public website modules — House Prices, Planning Applications, and
          Planning Appeals. A cached response is reused instead of calling the AI/external API
          again, saving time and money on repeated searches for the same postcode.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label htmlFor="cache-enabled-switch" className="text-sm font-medium">Allow cached responses</Label>
            <p className="text-xs text-muted-foreground mt-0.5">When off, every search always fetches a fresh result.</p>
          </div>
          <Switch
            id="cache-enabled-switch"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="cache-enabled-switch"
          />
        </div>
        <div>
          <Label htmlFor="cache-max-age-input" className="text-sm font-medium">Reuse cached results for up to (days)</Label>
          <Input
            id="cache-max-age-input"
            type="number"
            min={1}
            max={365}
            value={maxAgeDays}
            onChange={handleDaysChange}
            onBlur={handleDaysBlur}
            disabled={!enabled || saving}
            className="max-w-[120px] mt-1.5"
            data-testid="cache-max-age-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Results older than this are treated as stale and a fresh lookup is run instead.
          </p>
        </div>
      </CardContent>
    </Card>

    <Card className="mt-6" data-testid="cache-entries-section">
      <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="text-base">Cached Entries ({total})</CardTitle>
          <CardDescription>What's currently cached, and when it will go stale.</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadEntries} data-testid="cache-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={entries.length === 0} data-testid="cache-clear-all-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear {moduleFilter ? MODULE_LABELS[moduleFilter] : "All"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear cached entries?</AlertDialogTitle>
                <AlertDialogDescription>
                  {moduleFilter
                    ? `This removes all cached ${MODULE_LABELS[moduleFilter]} results. The next search for any of these postcodes will run fresh.`
                    : "This removes ALL cached results across House Prices, Planning Applications, and Planning Appeals. The next search for any postcode will run fresh."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cache-clear-all-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearAll} data-testid="cache-clear-all-confirm">Clear</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          {MODULE_FILTERS.map((m) => (
            <button
              key={m || "all"}
              onClick={() => setModuleFilter(m)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${moduleFilter === m ? "bg-primary text-primary-foreground border-primary" : "border-input hover:bg-muted/40"}`}
              data-testid={`cache-filter-${m || "all"}`}
            >
              {m ? MODULE_LABELS[m] : "All"}
            </button>
          ))}
        </div>

        {entriesLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4 text-center" data-testid="cache-entries-empty">
            No cached entries{moduleFilter ? ` for ${MODULE_LABELS[moduleFilter]}` : ""} yet.
          </p>
        ) : (
          <Table data-testid="cache-entries-table">
            <TableHeader>
              <TableRow>
                <TableHead>Module</TableHead>
                <TableHead>Search</TableHead>
                <TableHead>Contents</TableHead>
                <TableHead>Cached On</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`cache-entry-row-${e.id}`}>
                  <TableCell className="text-xs">{MODULE_LABELS[e.module] || e.module}</TableCell>
                  <TableCell className="font-mono text-xs">{e.key}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.summary}</TableCell>
                  <TableCell className="text-xs">{formatDMY(e.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => deleteEntry(e.id)} data-testid={`cache-entry-delete-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </>
  );
}
